// Shared chrome for the Company settings cards: a head carrying the title,
// subtitle and Edit button, and — while editing — a full-bleed footer with the
// saved indicator and the Cancel/Save pair. Both editable cards render exactly
// this, so it lives here rather than being copied twice.
//
// It owns no draft state: `editing`, `dirty` and `saving` are the card's, which
// is what lets the two cards edit and save independently of each other.

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Icon } from '@/components/common';

export interface SettingsCardProps {
  title: string;
  subtitle: string;
  editing: boolean;
  dirty: boolean;
  saving: boolean;
  canSave: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  children: ReactNode;
}

const SettingsCard = ({
  title,
  subtitle,
  editing,
  dirty,
  saving,
  canSave,
  onEdit,
  onCancel,
  onSave,
  children,
}: SettingsCardProps) => {
  const { t } = useTranslation();

  return (
    <Card
      padded={false}
      head={
        <div className='company-settings__card-head'>
          <span className='company-settings__card-heading'>
            <span className='company-settings__card-title'>{title}</span>
            <span className='company-settings__card-subtitle'>{subtitle}</span>
          </span>
          {!editing && (
            <Button variant='secondary' size='sm' iconStart='pencil' onClick={onEdit}>
              {t('action_edit')}
            </Button>
          )}
        </div>
      }
    >
      {children}
      {editing && (
        <div className='company-settings__footer'>
          {!dirty && (
            <span className='company-settings__saved'>
              <Icon name='check' size={15} />
              {t('guest_all_saved')}
            </span>
          )}
          <span className='company-settings__footer-actions'>
            <Button variant='secondary' onClick={onCancel} disabled={saving}>
              {t('button_cancel')}
            </Button>
            <Button iconStart='check' onClick={onSave} loading={saving} disabled={!canSave}>
              {t('guest_save_changes')}
            </Button>
          </span>
        </div>
      )}
    </Card>
  );
};

export interface SettingsRowProps {
  label: string;
  /** Rendered as-is; pass '—' for an empty value rather than an empty string. */
  value: string;
  /** Shows a lock glyph — the field exists but cannot be edited. */
  locked?: boolean;
  /** Force LTR for values that are never Arabic (codes, emails, URLs). */
  ltr?: boolean;
  /** Render the value in the monospace pill treatment (hex, identifiers). */
  mono?: boolean;
  /** Hex colour rendered as a chip before the value. */
  swatch?: string;
}

/** One read-mode label/value block. */
export const SettingsRow = ({ label, value, locked, ltr, mono, swatch }: SettingsRowProps) => (
  <div className='company-settings__row'>
    <p className='company-settings__label'>{label}</p>
    <p className='company-settings__value' {...(ltr ? { dir: 'ltr' } : {})}>
      {swatch && (
        <span
          className='company-settings__swatch-chip'
          style={{ backgroundColor: swatch }}
          aria-hidden='true'
        />
      )}
      {mono ? <span className='company-settings__mono'>{value || '—'}</span> : value || '—'}
      {locked && <Icon name='lock' size={14} className='company-settings__lock' />}
    </p>
  </div>
);

export default SettingsCard;
